// web/src/components/Gleam.tsx
// A one-shot "gleam" that sweeps diagonally across its parent when it MOUNTS — an
// auto-play-on-appear take on React Bits' GlareHover (same angled band + sizing,
// but triggered on mount via a CSS keyframe instead of :hover). Render it as an
// absolutely-positioned overlay inside a `relative`/`overflow` parent; give it a
// matching border-radius via className and key it to replay when content changes.
import "./Gleam.css";
import type { CSSProperties, JSX } from "react";

export default function Gleam({
  color = "#ffffff",
  opacity = 0.3,
  angle = -30,
  size = 300,
  duration = 800,
  delay = 180,
  className = "",
}: {
  color?: string; // hex glare color
  opacity?: number; // 0–1
  angle?: number; // degrees
  size?: number; // % (glare tile size, e.g. 300 = 300%)
  duration?: number; // ms sweep
  delay?: number; // ms before the sweep starts (lets the entrance land first)
  className?: string;
}): JSX.Element {
  // hex → rgba (mirrors GlareHover's parsing).
  const hex = color.replace("#", "");
  let rgba = color;
  if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    rgba = `rgba(${r}, ${g}, ${b}, ${opacity})`;
  } else if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    rgba = `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  const vars = {
    "--gleam-angle": `${angle}deg`,
    "--gleam-duration": `${duration}ms`,
    "--gleam-delay": `${delay}ms`,
    "--gleam-size": `${size}%`,
    "--gleam-rgba": rgba,
  } as CSSProperties;

  return <span aria-hidden="true" className={`gleam ${className}`} style={vars} />;
}
